import { z } from 'zod';
// Helpers — soft caps for agent-output text fields.
//
// Background: Sonnet routinely overshoots tight character + array
// budgets in structured output by 1-3 chars or 1-2 items. A hard
// .max(N) rejects with ZodError → upstream sees "agent_crashed"
// → the founder loses the whole 60-100s pass over a soft product
// preference (was 281 chars, we want ≤280).
//
// Strategy: keep `min()` strict (a too-short value IS garbage we
// want to reject), but flip `max()` to a `.transform()` that
// silently truncates. The system prompt still hard-codes the caps
// so the model stays aimed; the transform is a safety net.
//
// Apply on AGENT-OUTPUT strings + arrays. Don't soften INPUT-side
// caps (founder URLs, brand brief arrays) — those we control, hard
// rejection is the right behavior.
const softMaxStr = (max) => z.string().transform((s) => (s.length > max ? s.slice(0, max) : s));
const softCapStr = (min, max) => z
    .string()
    .min(min)
    .transform((s) => (s.length > max ? s.slice(0, max) : s));
// [12.2L-170] Competition analyst — structured output of mag's
// competitive landscape pass. Read by the strategist (which gaps to
// target), brand-coach (when to suggest re-running the analysis), and
// the content-plan agent (which content angles are underserved).
//
// The agent itself lives in @magig/agents/agents/competition-analyst.
// This file is just the contract.
//
// Persists as a new jsonb column on brand_brains (`competitive_landscape`,
// nullable). Founder edits via the dashboard's brand-brain UI. The
// agent re-runs quarterly or on-demand and proposes a diff the founder
// reviews via a feed_card.
export const competitiveCategorySchema = z.enum([
    'direct', // same product/service, same audience
    'indirect', // alternative solution the customer might choose
    'search', // ranks for the same keywords but isn't a traditional competitor
]);
export const competitiveSourceSchema = z.enum([
    'founder_named', // founder typed their name/url
    'mag_discovered', // mag found via search (v2 — discovery not in v1)
]);
/** Founder-driven workflow status for competitor rows. Mirrors the
 *  recommendation status: the agent emits all rows as `pending`; the
 *  founder agrees (status='accepted') during onboarding step 4 to
 *  endorse mag's read. Disagreement = discard (filter from array),
 *  not a separate status — keeping a "rejected" status would surface
 *  the row again on the next refresh, which fights the founder's
 *  signal. */
export const competitiveCompetitorStatusSchema = z.enum(['pending', 'accepted']);
/** One competitor mag has researched. Free-form prose for the
 *  qualitative reads (SEO, social, paid) — we DON'T claim quantitative
 *  numbers we can't verify from the public site alone. v2 wires
 *  Ahrefs/SimilarWeb integrations for real metrics; v1 is honest
 *  qualitative ("dominates terms around X" not "ranks #1 for 47 kws"). */
export const competitiveCompetitorSchema = z.object({
    /** Stable id stamped server-side after the agent emits. Mirrors the
     *  recommendation id pattern — coerceLandscape fills it before
     *  parse. Optional for back-compat with rows persisted before the
     *  field was introduced. */
    id: z.string().min(1).max(40).optional(),
    /** Founder-facing name. */
    name: softCapStr(1, 120),
    /** Canonical homepage. Stamped after normalizeUrl(). Stays strict
     *  — a URL can't be safely sliced (slicing chops a valid URL into
     *  an invalid one). If the agent emits a 501-char URL that's real
     *  garbage worth failing. */
    url: z.string().url().max(500),
    category: competitiveCategorySchema,
    /** Where mag got this name from. */
    source: competitiveSourceSchema,
    /** One-sentence read of how they position themselves. Mag voice.
     *  Nullable like the other qualitative reads (pricing/seoNotes/…): when
     *  mag can't read a competitor's positioning from the scrape she emits
     *  null rather than fabricating — and a null must NOT crash the whole
     *  pass (it did: a null here threw out every competitor + the market's
     *  card, amplified by the per-market fan-out). */
    positioning: softMaxStr(280).nullable(),
    /** What this competitor does well — 0-5 short bullets. Each bullet
     *  MUST be grounded in something visible in the scrape (a paraphrase
     *  of a section, a value-prop quote, an observable pattern). Empty
     *  when the scrape doesn't surface clear strengths — empty is honest.
     *  cap is a transform (not .max) so the model occasionally returning
     *  6-7 items doesn't blow up the whole pass — we truncate to 5 and
     *  carry on. the prompt still says "max 5" to keep the model aimed. */
    strengths: z.array(softCapStr(5, 280)).transform((a) => a.slice(0, 5)),
    /** Where they fall short — 0-5 short bullets. Each bullet MUST be
     *  grounded in something visible in (or notably MISSING from) the
     *  scrape. Empty when nothing obvious surfaces (refusing to fabricate
     *  weaknesses is the right answer). */
    weaknesses: z.array(softCapStr(5, 280)).transform((a) => a.slice(0, 5)),
    /** Pricing model summary when discoverable from the public site
     *  (a "Pricing" page, a published rate card, a clear free tier).
     *  Null when not visible — mag won't guess at numbers. */
    pricing: softMaxStr(280).nullable(),
    /** SEO observations from the site itself — themes they appear to
     *  cover heavily (content cadence, topic clusters, content depth).
     *  NOT keyword counts; this is qualitative. */
    seoNotes: softMaxStr(560).nullable(),
    /** Social presence read — platforms named on the site + linked
     *  profiles, cadence observable from the recent posts when public,
     *  format mix. Null when no social links surface. */
    socialNotes: softMaxStr(560).nullable(),
    /** Paid ads observed — best-effort from Meta Ad Library + Google
     *  Ads transparency when the agent can verify them. Null otherwise
     *  (which is the common case in v1 since we don't have those
     *  integrations wired). */
    paidNotes: softMaxStr(560).nullable(),
    /** Mag's confidence on this competitor's read, 0-10. Low when she
     *  could only fetch the homepage and couldn't see pricing/about. */
    confidence: z.number().int().min(0).max(10),
    /** ISO timestamp of the research pass that produced this entry. */
    researchedAt: z.string(),
    /** Founder workflow status. Optional for back-compat. */
    status: competitiveCompetitorStatusSchema.optional(),
    /** ISO timestamp the founder marked this competitor accepted. */
    acceptedAt: z.string().optional(),
});
/** Market-level SWOT mag synthesizes across the competitor set. The
 *  brand IS the subject — "strengths/weaknesses" are the BRAND's,
 *  measured against the competitive set; "opportunities/threats" are
 *  the market's gaps mag wants the brand to exploit / watch for. */
export const competitiveSwotSchema = z.object({
    // soft caps via transform — see competitor.strengths note above.
    strengths: z.array(softCapStr(10, 280)).transform((a) => a.slice(0, 6)),
    weaknesses: z.array(softCapStr(10, 280)).transform((a) => a.slice(0, 6)),
    opportunities: z.array(softCapStr(10, 280)).transform((a) => a.slice(0, 6)),
    threats: z.array(softCapStr(10, 280)).transform((a) => a.slice(0, 6)),
});
/** One actionable recommendation. Tagged by which existing agent /
 *  surface owns the action so the dashboard can route the founder
 *  ("act on this" → opens the relevant settings page or queues a
 *  feed card). */
export const competitiveRecommendationActionableSchema = z.enum([
    'content', // belongs in content plan
    'paid', // belongs in paid strategy
    'positioning', // belongs in brand brain identity
    'pricing', // belongs in product/services
    'brand', // belongs in brand brain voice / design
    'product', // product-side change (out of mag's scope, surfaced to founder)
]);
/** Founder-driven workflow status. The agent emits all rows as
 *  `pending`; the founder triages each one on the results surface:
 *    - 'pending'  — initial state. Awaiting review.
 *    - 'accepted' — founder agreed; ground truth for downstream
 *                   coaching agents (content, paid, positioning).
 *    - 'skipped'  — founder dismissed; persists so the next
 *                   competition refresh doesn't surface the same
 *                   recommendation again unless mag has new evidence. */
export const competitiveRecommendationStatusSchema = z.enum(['pending', 'accepted', 'skipped']);
/** [Layer 2] Founder-bound handoff artifact mag emits when the
 *  founder hits "draft this →" on an accepted recommendation. Same
 *  shape across all six `actionable` kinds — what varies is the
 *  CONTENT of `agentPrompt` and `humanMarkdown`, not the schema.
 *
 *  The draft is intentionally IMMUTABLE from the dashboard's POV:
 *  the founder doesn't edit copy in-app, they take the artifacts
 *  out (Cursor / a designer / a media buyer) and act there. The one
 *  escape hatch is `refineNote` — the founder's free-text guidance
 *  that gets fed to the next regenerate pass. */
export const recommendationDraftStatusSchema = z.enum([
    /** Just generated, matches the rec it came from. */
    'fresh',
    /** Quarterly refresh produced a rec with the same id but different
     *  headline/detail — `generatedFromRecHash` mismatches. UI offers
     *  regenerate without clobbering until the founder asks. */
    'stale-from-refresh',
    /** Founder dismissed the draft. Persists on the rec so the next
     *  quarterly pass doesn't surface the exact same suggestion again. */
    'archived',
]);
export const recommendationDraftSchema = z.object({
    /** Copy-paste-ready prompt for a downstream coding/AI agent (Cursor,
     *  Claude Code, etc.). Embeds the relevant brand-context + hallucination
     *  guard so the downstream agent can't "improve" the spec with
     *  fabricated extras. */
    agentPrompt: softCapStr(20, 20_000),
    /** Markdown brief addressed to a HUMAN teammate (dev, designer,
     *  media buyer, agency). Mag-voice rationale + the actionable
     *  contents in brand voice. Downloadable / shareable. */
    humanMarkdown: softCapStr(20, 20_000),
    /** ISO timestamp when the draft was produced. */
    generatedAt: z.string(),
    /** Hash of the rec's headline + detail at the time of generation.
     *  When the next competition pass re-emits this rec with edits,
     *  the hash mismatches and status flips to 'stale-from-refresh'. */
    generatedFromRecHash: z.string().min(1).max(40),
    /** Founder's free-text guidance for regeneration ("we already
     *  tried that approach", "make sure to mention X"). When set, fed
     *  into the next regenerate run as additional context. */
    refineNote: z.string().max(2000).optional(),
    /** Workflow status. */
    status: recommendationDraftStatusSchema.optional(),
});
export const competitiveRecommendationSchema = z.object({
    /** Stable id stamped server-side after the agent emits. The agent
     *  itself does NOT generate this — coerceLandscape (in
     *  apps/api/src/agents/competition-analyst.ts) fills it before the
     *  parse. Optional so existing landscape rows from before the field
     *  was introduced still validate. */
    id: z.string().min(1).max(40).optional(),
    // soft caps — overshoot truncates rather than crashing. previously
    // bumped these (140→220, 560→800) chasing the same overshoot
    // problem; the helpers now handle it generically.
    headline: softCapStr(10, 220),
    detail: softCapStr(20, 800),
    actionable: competitiveRecommendationActionableSchema,
    /** Which competitor(s) this rec is responding to. References by
     *  competitor.name (NOT url) for legibility. Empty when the rec is
     *  market-wide. */
    referencingCompetitors: z.array(softCapStr(1, 120)).transform((a) => a.slice(0, 6)),
    /** Founder workflow status. Optional for back-compat. */
    status: competitiveRecommendationStatusSchema.optional(),
    /** ISO timestamp the founder marked this rec accepted. */
    acceptedAt: z.string().optional(),
    /** [Layer 2] Coaching draft — populated when the founder hits
     *  "draft this →" on an accepted rec. Single shape across all
     *  six `actionable` kinds; see recommendationDraftSchema. */
    draft: recommendationDraftSchema.optional(),
});
/** The full competitive landscape — what gets persisted on
 *  brand_brain.competitive_landscape and what the agent emits. */
export const competitiveLandscapeSchema = z.object({
    // agent-output array caps — soft via transform so overshoot
    // truncates rather than blowing up the whole 60-105s pass. the
    // prompt still hard-codes "max 12 competitors / 10 recommendations"
    // to keep the model aimed.
    competitors: z.array(competitiveCompetitorSchema).transform((a) => a.slice(0, 12)),
    swot: competitiveSwotSchema,
    recommendations: z.array(competitiveRecommendationSchema).transform((a) => a.slice(0, 10)),
    /** Mag's overall confidence on the pass, 0-10. Lower when she
     *  could only fetch a few competitor sites OR when the founder
     *  named so few competitors that discovery would have helped. */
    confidence: z.number().int().min(0).max(10),
    /** Summary mag writes for the feed card + the onboarding results
     *  screen. Lowercase, mag voice. Cap is generous (800 chars) so a
     *  3-4 sentence summary in Spanish/longer-form languages fits
     *  without truncation — the prior 280-char cap forced a mid-word
     *  cut on real summaries (s216-54h.8). */
    summary: softCapStr(10, 800),
    /** ISO timestamp the full pass landed. Drives the "refresh
     *  competitors" UI's last-updated label + the quarterly cadence. */
    lastFullPassAt: z.string(),
});
// ─── Agent IO ──────────────────────────────────────────────────────
/** What the competition-analyst agent takes as input. Either named
 *  competitors (URLs the founder supplied) OR auto-discover via the
 *  Anthropic web_search server-side tool. */
export const competitionAnalystInputSchema = z.object({
    /** Brand context — name, category, value props, products. The
     *  agent uses this to judge what "direct" / "indirect" means for
     *  this brand. */
    brandContext: z.object({
        companyName: z.string().min(1).max(200),
        category: z.string().min(1).max(120),
        positioningStatement: z.string().max(560).nullable(),
        valueProps: z.array(z.string().min(1).max(280)).max(8),
        /** Product/service names the brand offers — informs comparison. */
        productNames: z.array(z.string().min(1).max(120)).max(12),
        /** Target market — geo + language hint mag at competitor scope. */
        targetCountry: z.string().length(2).nullable(),
        targetLanguage: z.string().min(2).max(8).nullable(),
        /** [Layer 1+] ICP — top 1-2 customer segments mag has captured.
         *  Each segment carries the founder's read of who they sell to:
         *  name, pains, and JTBD. The agent uses this to score whether a
         *  recommendation fits THIS brand's actual customer — generic
         *  "improve SEO" gets dropped in favor of "publish a SAS-vs-SRL
         *  comparison page" when the ICP is freelancers picking entity
         *  type. Optional + bounded for back-compat with brains that
         *  don't have customer.icp_segments populated yet. */
        icpSegments: z
            .array(z.object({
            name: z.string().min(1).max(160),
            pains: z.array(z.string().min(1).max(280)).max(5),
            jobsToBeDone: z.array(z.string().min(1).max(280)).max(5),
        }))
            .max(2)
            .optional(),
        /** [Layer 1+] Voice anchor — tone descriptors + the founder's
         *  must-use / must-avoid words. The agent uses this to write rec
         *  headlines + details in the brand's voice (the existing voice
         *  rules in the system prompt are MAG'S voice; this is the
         *  brand's). When absent, mag-voice defaults apply. */
        voice: z
            .object({
            tone: z.array(z.string().min(1).max(40)).max(5).optional(),
            mustUseWords: z.array(z.string().min(1).max(80)).max(8).optional(),
            mustAvoidWords: z.array(z.string().min(1).max(80)).max(8).optional(),
        })
            .optional(),
        /** [Layer 1+] Top testimonial quotes — gives the agent ground
         *  truth on what the brand's actual customers say. Used as a
         *  reality-check on recommendations: if a rec contradicts the
         *  voice/sentiment in the testimonials, mag should pivot. */
        testimonialExcerpts: z.array(z.string().min(1).max(400)).max(3).optional(),
    }),
    /** Founder-named competitor URLs. Can be empty IF autoDiscoverCount
     *  > 0 (mag will find some via web search). At least one of the
     *  two must produce a non-empty list. */
    namedCompetitorUrls: z.array(z.string().url().max(500)).max(8),
    /** [12.2L-170] When > 0, the agent runs a phase-0 web_search pass
     *  to identify N additional plausible competitor URLs and folds
     *  them into the analysis. Costs ~$0.05-0.10 per pass (Anthropic
     *  web_search pricing). 0 = skip discovery; use namedCompetitorUrls
     *  only. */
    autoDiscoverCount: z.number().int().min(0).max(8).default(0),
    /** [12.2L-171] Real SEO data from Google Keyword Planner — read
     *  from the brand's cached strategist research when present. The
     *  agent grounds seoNotes with actual monthly volume + competition
     *  numbers per theme instead of pure qualitative observations. Empty
     *  array when no cached research exists yet (the agent then falls
     *  back to qualitative reads). */
    kpContext: z
        .array(z.object({
        keyword: z.string().min(1).max(120),
        monthlyVolume: z.number().int().min(0),
        competition: z.enum(['low', 'medium', 'high', 'unknown']),
    }))
        .max(50)
        .optional(),
    /** Optional founder hint about which competitors they consider
     *  direct vs indirect. Mag re-classifies in the output. */
    founderHints: z.string().max(560).optional(),
});
//# sourceMappingURL=competition-analyst.js.map